/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import styled from '@emotion/styled';
import {
  FormFieldValidation,
  GroupAlignment,
  RadioButtonGroup,
  Text,
} from '@sonarsource/echoes-react';
import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';

interface BooleanRadioConfig {
  invalidTextKey?: string;
  noHelpTextKey: string;
  noLabelKey: string;
  titleKey: string;
  yesHelpTextKey: string;
  yesLabelKey: string;
}

interface GenericBooleanRadioSectionProps {
  config: BooleanRadioConfig;
  onChange: (value: boolean) => void;
  value: boolean | null;
}

export function GenericBooleanRadioSection({
  config,
  value,
  onChange,
}: Readonly<GenericBooleanRadioSectionProps>) {
  const intl = useIntl();
  const formatLabel = (labelKey: string) => capitalize(intl.formatMessage({ id: labelKey }));

  const options = [
    {
      helpText: intl.formatMessage({ id: config.yesHelpTextKey }),
      label: formatLabel(config.yesLabelKey),
      value: 'true',
    },
    {
      helpText: intl.formatMessage({ id: config.noHelpTextKey }),
      label: formatLabel(config.noLabelKey),
      value: 'false',
    },
  ];

  return (
    <StyledRadioGroup
      alignment={GroupAlignment.Vertical}
      className="sw-mt-6"
      isRequired
      label={
        <Text className="sw-inline-block sw-mb-3" isHighlighted>
          {intl.formatMessage({ id: config.titleKey })}
        </Text>
      }
      messageInvalid={
        config.invalidTextKey ? intl.formatMessage({ id: config.invalidTextKey }) : undefined
      }
      onChange={(selectedValue) => {
        onChange(selectedValue === 'true');
      }}
      options={options}
      validation={config.invalidTextKey ? FormFieldValidation.Invalid : undefined}
      value={value === null ? undefined : String(value)}
    />
  );
}

const StyledRadioGroup = styled(RadioButtonGroup)`
  [role='radiogroup'] > div > div {
    width: 50%;
  }
`;
